<template>
  <!-- Sanne (Figma iPad 5599:19720, mobile 5598:19319): an arched card with the
       logo, the copy and four reminder icons, the Sanne wordmark below -->
  <div v-if="isSanne" class="end-root sanne-end" @click="goBack">
    <div class="sanne-end-card">
      <img class="sanne-end-logo" :src="sanneArt.logo" alt="" />
      <div class="sanne-end-content">
        <div class="sanne-end-copy">
          <h1 class="sanne-end-title">{{ title }}</h1>
          <p class="sanne-end-hint" v-html="text"></p>
        </div>
        <div class="sanne-end-reminders">
          <img class="sanne-end-line" :src="sanneArt.line" alt="" />
          <div class="sanne-end-icons">
            <img v-for="icon in sanneIcons" :key="icon" :src="icon" alt="" />
          </div>
        </div>
      </div>
    </div>
    <div class="sanne-end-brand">
      <img :src="sanneArt.wordmark" alt="Sanne" />
      <!-- brand tagline, kept in English in every language -->
      <p>Engineered for Everyday Legends</p>
    </div>
    <leave-button @click.stop="goBack" />
  </div>
  <div
    v-else
    class="end-root flex-col w-full h-full flex justify-start items-center px-6 md:px-20"
    @click="goBack"
  >
    <div class="end-titles flex flex-col w-full text-center mt-16 md:mt-32">
      <h1 class="screen-info text-2xl md:text-4xl">{{ title }}</h1>
      <h2 class="screen-name mt-8" v-html="text"></h2>
    </div>
    <div class="end-art flex flex-1 justify-center w-3/4">
      <img :src="endImg" />
    </div>
    <leave-button @click="goBack" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import i18n from '@/i18n';
import page from '@/mixins/page';
import audioPlayer from '@/mixins/audio-player';
import LeaveButton from '@/components/common/LeaveButton.vue';
import brandMode from '@/mixins/brand-mode';
import endImg from '@/assets/Ending/end.svg';
import sanneLogo from '@/assets/sanne/thank-you/logo.svg';
import sanneLine from '@/assets/sanne/thank-you/line.svg';
import sanneBag from '@/assets/sanne/thank-you/bag.svg';
import sannePhone from '@/assets/sanne/thank-you/phone.svg';
import sanneEarrings from '@/assets/sanne/thank-you/earrings.svg';
import sanneDoor from '@/assets/sanne/thank-you/door.svg';
import sanneWordmark from '@/assets/sanne/thank-you/wordmark.svg';

export default {
  name: 'End',
  components: { LeaveButton },
  mixins: [page, audioPlayer, brandMode],
  data: () => ({
    endImg,
    sanneArt: {
      logo: sanneLogo,
      line: sanneLine,
      wordmark: sanneWordmark,
    },
    // bag, phone, earrings, door: each a 72 × 72 Figma icon frame
    sanneIcons: [sanneBag, sannePhone, sanneEarrings, sanneDoor],
  }),
  mounted() {
    this.playAudio('19-Thank_you');
    this.timer = setTimeout(
      () => {
        this.goBack();
      },
      this.isHM ? 8000 : 15000
    );
  },
  beforeDestroy() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  },
  methods: {
    goBack() {
      if (this.exitOnScanComplete) {
        this.$events.goto('Close');
      } else {
        this.$events.goto(this.isContactless ? 'Welcome' : 'HomePage');
      }
    },
  },
  computed: {
    ...mapState('app', ['exitOnScanComplete']),
    title() {
      if (this.isHM) return i18n.t('hm.end_title');
      if (this.isSanne) return i18n.t('sanne.end_title');
      return i18n.t('end.thank');
    },
    text() {
      if (this.isHM) return i18n.t('hm.end_text');
      if (this.isSanne) return i18n.t('sanne.end_hint');
      return i18n.t('end.belonging');
    },
  },
};
</script>

<style lang="scss" scoped>
.screen-name {
  @apply text-base w-4/5 mx-auto;
  @screen md {
    @apply text-2xl;
    line-height: 1.67;
  }
  line-height: 1.25;
  color: #262626;
}
.screen-info {
  font-weight: bold;
  line-height: 1.39;
  color: #2c2c2c;
}

// ---- Sanne -------------------------------------------------------------
// Figma frames carry the status bar (44 on phones, 24 on iPad); positions
// are taken from the safe area like every Sanne screen.
.sanne-end {
  @apply relative flex flex-col items-center w-full h-full overflow-y-auto;
  background-color: var(--color-surface);
  color: var(--color-text);
  padding: calc(var(--safe-top) + 24px) 0 calc(var(--safe-bottom) + 24px);
  @screen lg {
    // equal rows above and below the card centre it on the screen; the
    // wordmark sits at the bottom of the lower one, 95 above the edge (Figma)
    display: grid;
    grid-template-rows: 1fr auto 1fr;
    justify-items: center;
    padding: calc(var(--safe-top) + 95px) 0 calc(var(--safe-bottom) + 95px);
  }
}

// arch: the top corners are a 480 radius, the bottom ones 8
.sanne-end-card {
  @apply flex flex-col items-center flex-shrink-0;
  // phones: margin auto centres it with the wordmark under it
  margin-top: auto;
  width: 320px;
  padding: 72px 24px;
  gap: 48px;
  border-radius: 480px 480px 8px 8px;
  background-color: var(--color-logo); // Desert Suede #f0dba8
  @screen lg {
    grid-row: 2;
    margin-top: 0;
    width: 594px;
    height: 815px;
    padding: 132px 0;
    gap: 72px;
  }
}

.sanne-end-logo {
  width: 46px;
  height: 46px;
  @screen lg {
    width: 72px;
    height: 72px;
  }
}

.sanne-end-content {
  @apply flex flex-col items-center;
  width: 280px;
  gap: 48px;
  @screen lg {
    width: 480px;
    gap: 148px;
  }
}

.sanne-end-copy {
  @apply flex flex-col items-center w-full text-center;
  gap: 24px;
}

.sanne-end-title {
  min-height: 56px;
  font-family: var(--font-heading);
  font-weight: 500;
  font-size: 20px;
  line-height: normal;
  @screen lg {
    font-size: 24px;
  }
}

.sanne-end-hint {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 16px;
  line-height: normal;
  @screen lg {
    font-weight: 500;
    font-size: 20px;
    line-height: 30px;
  }
}

.sanne-end-reminders {
  @apply flex flex-col w-full;
  gap: 48px;
}

.sanne-end-line {
  @apply block w-full;
  height: 1px;
  @screen lg {
    height: 2px;
  }
}

.sanne-end-icons {
  @apply flex items-center justify-between w-full;
  padding: 0 16px;
  @screen lg {
    padding: 0 24px;
  }
}

// 48 on phones, 72 on iPad
.sanne-end-icons img {
  width: 48px;
  height: 48px;
  @screen lg {
    width: 72px;
    height: 72px;
  }
}

// wordmark + tagline: 64 under the card on phones, at the bottom on iPad
.sanne-end-brand {
  @apply flex flex-col items-center flex-shrink-0;
  gap: 12px;
  margin: 64px 0 auto;
  img {
    width: 103px;
    height: 14px;
  }
  p {
    font-family: var(--font-heading);
    font-weight: 500;
    font-size: 11px;
    line-height: normal;
    color: var(--color-primary);
  }
  @screen lg {
    grid-row: 3;
    align-self: end;
    gap: 24px;
    margin: 0;
    img {
      width: 176px;
      height: 24px;
    }
    p {
      font-size: 14px;
    }
  }
}
</style>
